import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDateRangeAsObjects } from 'features/date-range-selection/dateRangeSlice';
import { RootState } from 'app/store';
import TileGridContainer from 'features/tiles/tileGridContainer';
import DateRangePicker from 'features/date-range-selection/dateRangePicker';
import TileWithoutDefaultStyling from 'features/tiles/tileWithoutDefaultStyling';
import Button from 'common/components/buttons/button';
import { useRunClientIntegrationMutation } from 'app/apis/commandApi';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { selectIntegrationByKey } from 'features/client-integrations/clientIntegration-meta-slice';
import { onError, onSuccess } from 'features/toast/toast.slice';

export const RunIntegrationPage: FC = () => {
  const { siteId } = useSelector((state: RootState) => state.selectedSite);

  const { platformCategory, platform, clientIntegrationType } = useParams<{
    platformCategory: string;
    platform: string;
    clientIntegrationType: string;
  }>();

  const createPlatformKey = (platform: string | undefined): string => {
    if (!platform) {
      return '';
    }
    return platform
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
  };

  const formattedPlatform = (platform ?? '').replace(/-/g, '');

  const integrationMeta = useSelector(
    selectIntegrationByKey(createPlatformKey(platform) as any)
  );

  const dateRange = useSelector(getDateRangeAsObjects);

  const [runClientIntegration, { isLoading, error }] =
    useRunClientIntegrationMutation();

  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const dispatch = useDispatch();

  const handleSubmit = async () => {
    setSuccessMessage(null);

    try {
      const response = await runClientIntegration({
        siteId,
        platformCategory,
        platform: formattedPlatform,
        clientIntegrationType,
        body: {
          startDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
          endDate: moment(dateRange.endDate).format('YYYY-MM-DD')
        }
      }).unwrap();

      const message = `${response.Message} Days to process: ${response.DaysProcessed}`;
      setSuccessMessage(message);

      dispatch(
        onSuccess({
          title: 'Integration Triggered Successfully',
          description: message
        })
      );
    } catch (err: any) {
      const message =
        err?.data?.Message || 'Something went wrong. Please try again.';

      dispatch(
        onError({
          title: 'Integration Failed To Trigger',
          description: message
        })
      );
    }
  };

  const integrationName = integrationMeta?.name || '';
  const integrationLogo = integrationMeta?.logo || '';

  return (
    <TileGridContainer>
      <TileWithoutDefaultStyling className="bg-white border rounded border-neutral-200 p-2.5 shadow-sm w-full grow basis-full flex justify-center items-center">
        <section className="mx-6 my-6 w-full max-w-2xl">
          <div className="flex items-center justify-between border-b border-neutral-200 pb-4 mb-4 mt-2">
            <h2 className="text-primary-700 text-l">
              {integrationLogo && (
                <img
                  src={integrationLogo}
                  alt={integrationName}
                  className="inline-block w-8 h-8 -mt-1 mr-2"
                />
              )}
              {integrationName
                ? `Run ${integrationName} Integration`
                : 'Run Integration'}
            </h2>
          </div>

          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-sm text-primary-700 mb-1">
                Select Date Range (Max 1 Month)
              </label>
              <div className="w-full">
                <DateRangePicker />
              </div>
            </div>

            <div>
              <Button
                theme={ButtonTheme.PRIMARY}
                identifier="run-integration-submit"
                type="submit"
                onClick={handleSubmit}
                isLoading={isLoading}
                isDisabled={isLoading}
              >
                <span>Run Integration</span>
              </Button>
              {successMessage && (
                <div>
                  <p className="text-success-400 mt-2">{successMessage}</p>
                  <p className="text-primary-700 mt-2">
                    Please check reports to verify costs, the processing is not
                    instant and can take some time.
                  </p>
                </div>
              )}
              {error && (
                <p className="text-error-300 mt-2">
                  Please ensure date range is no greater than 1 month
                </p>
              )}
            </div>
          </div>
        </section>
      </TileWithoutDefaultStyling>
    </TileGridContainer>
  );
};
